import { Box, Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { blue, green, grey, orange, purple } from "@mui/material/colors";
import { DefaultizedPieValueType } from "@mui/x-charts";
import DonutChart from "components/Dashboard/DonutChart";
import { WasteSubmissionsStaffSummaryQuery } from "generated-graphql/graphql";
import { useMemo } from "react";

export type SubmissionStatusId =
  | "NOT_STARTED"
  | "IN_PROGRESS"
  | "ENCAMP_INITIAL_REVIEW"
  | "IN_REVIEW"
  | "PROCESSING_SUBMISSION"
  | "FILING_COMPLETE"
  | "NOT_REPORTING";

type Props = {
  data?: WasteSubmissionsStaffSummaryQuery["wasteSubmissionsStaffSummary"];
  loading: boolean;
  onStatusClick?: (statusId: SubmissionStatusId) => void;
  selectedStatusId?: SubmissionStatusId | null;
};

const STATUS_COLORS: Record<SubmissionStatusId, string> = {
  NOT_STARTED: orange[400],
  IN_PROGRESS: blue[400],
  ENCAMP_INITIAL_REVIEW: purple[300],
  IN_REVIEW: blue[200],
  PROCESSING_SUBMISSION: purple[500],
  FILING_COMPLETE: green[400],
  NOT_REPORTING: grey[400],
};

const STATUS_COLORS_INACTIVE: Record<SubmissionStatusId, string> = {
  NOT_STARTED: orange[100],
  IN_PROGRESS: blue[100],
  ENCAMP_INITIAL_REVIEW: purple[100],
  IN_REVIEW: blue[50],
  PROCESSING_SUBMISSION: purple[100],
  FILING_COMPLETE: green[100],
  NOT_REPORTING: grey[200],
};

const STATUS_LABELS: Record<SubmissionStatusId, string> = {
  NOT_STARTED: "Not Started",
  IN_PROGRESS: "In Progress",
  ENCAMP_INITIAL_REVIEW: "Initial Review",
  IN_REVIEW: "In Review",
  PROCESSING_SUBMISSION: "Processing",
  FILING_COMPLETE: "Complete",
  NOT_REPORTING: "Not Reporting",
};

const STATUS_ORDER: SubmissionStatusId[] = [
  "NOT_STARTED",
  "IN_PROGRESS",
  "ENCAMP_INITIAL_REVIEW",
  "IN_REVIEW",
  "PROCESSING_SUBMISSION",
  "FILING_COMPLETE",
  "NOT_REPORTING",
];

export function StatusDonutChart({ data, loading, onStatusClick, selectedStatusId }: Props) {
  const theme = useTheme();

  const hasSelection = selectedStatusId != null;

  const selectedIndex = useMemo(() => {
    if (!selectedStatusId) return null;
    return STATUS_ORDER.indexOf(selectedStatusId);
  }, [selectedStatusId]);

  const totalSubmissions = useMemo(() => {
    if (!data) return 0;

    if (hasSelection && selectedStatusId) {
      const statusData = data.byStatus.find((s) => s.status === selectedStatusId);
      return statusData?.count ?? 0;
    }

    return data.total;
  }, [data, hasSelection, selectedStatusId]);

  const seriesData = useMemo(() => {
    if (!data) return [];

    return STATUS_ORDER.map((statusId) => {
      const isSelected = !hasSelection || selectedStatusId === statusId;
      const statusData = data.byStatus.find((s) => s.status === statusId);
      const value = statusData?.count ?? 0;

      return {
        id: statusId,
        value,
        color: isSelected ? STATUS_COLORS[statusId] : STATUS_COLORS_INACTIVE[statusId],
        label: STATUS_LABELS[statusId],
      };
    });
  }, [data, hasSelection, selectedStatusId]);

  const legendItems = useMemo(() => {
    return seriesData.map((item) => {
      const isSelected = !hasSelection || selectedStatusId === (item.id as SubmissionStatusId);
      return {
        color: STATUS_COLORS[item.id as SubmissionStatusId],
        isSelected,
        value: (
          <Box
            display="flex"
            width="100%"
            justifyContent="space-between"
            gap={2}
            sx={{ opacity: isSelected ? 1 : 0.4 }}
          >
            <Typography variant="body2">{item.label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {item.value.toLocaleString()}
            </Typography>
          </Box>
        ),
      };
    });
  }, [seriesData, hasSelection, selectedStatusId]);

  const handleStatusClick = (item: DefaultizedPieValueType) => {
    if (onStatusClick) {
      onStatusClick(item.id as SubmissionStatusId);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 3,
        height: "100%",
        minHeight: 380,
      }}
    >
      {loading ? (
        <Box
          sx={{
            height: "100%",
            animation: "fadeIn 0.3s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary }}>
            Submissions by Status
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "calc(100% - 60px)",
              gap: 4,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 250,
                height: 250,
                flexShrink: 0,
              }}
            >
              <Skeleton
                variant="circular"
                width={250}
                height={250}
                sx={{
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1 },
                    "50%": { opacity: 0.4 },
                    "100%": { opacity: 1 },
                  },
                }}
              />
            </Box>
            <Stack spacing={1} sx={{ flex: 1 }}>
              {Array.from({ length: 7 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    animation: `slideIn 0.3s ease-out ${index * 0.05}s both`,
                    "@keyframes slideIn": {
                      from: {
                        opacity: 0,
                        transform: "translateX(-10px)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translateX(0)",
                      },
                    },
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width={16}
                    height={16}
                    sx={{ borderRadius: "2px", flexShrink: 0 }}
                  />
                  <Skeleton variant="text" sx={{ flex: 1 }} height={20} />
                  <Skeleton variant="text" width={40} height={20} />
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            height: "100%",
            animation: "fadeIn 0.5s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary }}>
            Submissions by Status
          </Typography>
          <DonutChart
            seriesData={seriesData}
            selectedIndex={selectedIndex}
            label={totalSubmissions.toLocaleString()}
            subLabel={hasSelection ? "Submissions" : "Total\nSubmissions"}
            containerHeight="18rem"
            legend={{
              direction: "column",
              position: "right",
            }}
            legendItems={legendItems}
            onItemClick={(event, pieItemIdentifier, item) => handleStatusClick(item)}
            onLegendItemClick={(item) => handleStatusClick(item)}
          />
        </Box>
      )}
    </Paper>
  );
}
